import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton';
import { FollowButton } from '../../components/global/FollowButton/FollowButton';
import { PdpSharedDeposit } from '../PdpSharedDeposit/PdpSharedDeposit';
import { addPageAction } from '../../utils/tracking/rumTracking';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { FollowSearch } from '../pdpMessages';
import { getDisplayInfo } from '../helpers/getDisplayInfo';
import {
    locations,
    trackFollowSearchAdded,
    trackFollowSearchRemoved,
} from '../../utils/tracking/shared/followSearchTracking';

// styles
import styles from './PdpRespSidebarButtons.scss';

// helpers
import get from 'lodash.get';
import classNames from 'classnames';

// messages
const followSearchText = searchName => (
    <p className={styles.followSearchText}>
        <FollowSearch searchName={searchName} searchNameClassname={styles.searchName} />
    </p>
);

const PdpRespSidebarButtonsComponent = props => {
    const { relayVariables, viewer, user, item, quantitySelected, skuId } = props;
    const { itemId, userId, isMultiSku } = relayVariables;
    const displayInfo = getDisplayInfo({
        displayInfo: props.displayInfo,
        skus: item.skus,
        selectedSkuId: skuId,
        isMultiSku,
    });

    if (!displayInfo || !displayInfo.ctas) {
        return null;
    }

    const {
        showPurchase,
        showMakeOffer,
        showRequestPrice,
        showFollowCategory,
        showAcceptOffer,
    } = displayInfo.ctas;
    const isVerifiedTrade = get(user, 'isVerifiedTrade') || false;
    const buttonsClass = classNames({ [styles.hasQuantitySelect]: !!quantitySelected });
    const followSearchName = get(item, 'saveSearchData.name');
    const followSearchPage = get(item, 'saveSearchData.page');
    const isSold = get(item, 'isSold');

    addPageAction('respPdpCTAsRendered', { itemId });

    return (
        <div className={buttonsClass} data-tn="item-actions">
            {/* Purchase */}
            {!!showPurchase && (
                <PdpSharedPurchaseButton
                    displayInfo={displayInfo}
                    itemId={itemId}
                    item={item}
                    userId={userId}
                    quantitySelected={quantitySelected}
                    user={user}
                    skuId={skuId}
                />
            )}
            {/* Request Price */}
            {!!showRequestPrice && (
                <PdpSharedContactDealerButton
                    type={'REQUEST_PRICE'}
                    item={item}
                    user={user}
                    viewer={viewer}
                />
            )}
            {/* Make Offer */}
            {showMakeOffer && (
                <CurrencyProvider
                    render={({ currency }) => (
                        <PdpSharedMakeOfferButton
                            itemId={itemId}
                            item={item}
                            userId={userId}
                            user={user}
                            quantitySelected={quantitySelected}
                            currency={currency}
                            hasPrivateOffer={showAcceptOffer}
                        />
                    )}
                />
            )}

            <PdpSharedDeposit userId={userId} item={item} />

            {/* Follow Search */}
            {showFollowCategory && isVerifiedTrade ? (
                // trade users
                <div className={styles.followTrade}>
                    {followSearchText(followSearchName)}
                    <div>
                        <FollowButton
                            size="small"
                            className={styles.followButtonTrade}
                            userId={userId}
                            favoritesConnection={get(props, 'user.favorites')}
                            page={followSearchPage}
                            name={followSearchName}
                            location={isSold && locations.SOLD_PDP}
                            onFollowSearchCompleted={isFollowing =>
                                isFollowing
                                    ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                    : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                            }
                        />
                    </div>
                </div>
            ) : (
                !!showFollowCategory && (
                    // non trade users
                    <div>
                        {followSearchText(followSearchName)}
                        <FollowButton
                            userId={userId}
                            user={user}
                            favoritesConnection={get(props, 'user.favorites') || null}
                            page={followSearchPage}
                            name={followSearchName}
                            fullWidth
                            location={isSold && locations.SOLD_PDP}
                            onFollowSearchCompleted={isFollowing =>
                                isFollowing
                                    ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                    : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                            }
                        />
                    </div>
                )
            )}
        </div>
    );
};

PdpRespSidebarButtonsComponent.propTypes = {
    relayVariables: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    isClient: PropTypes.bool,
    relay: PropTypes.object,
    userId: PropTypes.string,
    displayInfo: PropTypes.object,
    quantitySelected: PropTypes.string,
    currency: PropTypes.string,
    skuId: PropTypes.string,
};

export const PdpRespSidebarButtons = createFragmentContainer(PdpRespSidebarButtonsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarButtons_viewer on Viewer {
            ...PdpSharedContactDealerButton_viewer
        }
    `,
    user: graphql`
        fragment PdpRespSidebarButtons_user on User {
            isVerifiedTrade
            ...PdpSharedContactDealerButton_user
            ...PdpSharedMakeOfferButton_user
            favorites(first: 1, types: [SEARCH, USER_PREFERENCE], pages: $followSearchPages)
            @include(if: $fetchFollowSearch) {
                ...FollowButton_favoritesConnection
            }
        }
    `,
    item: graphql`
        fragment PdpRespSidebarButtons_item on Item {
            isSold
            isInProcurement(userId: $userId) @include(if: $fetchUser)
            saveSearchData {
                name
                page
            }
            ...PdpSharedContactDealerButton_item
            ...PdpSharedPurchaseButton_item
            ...PdpSharedMakeOfferButton_item
            ...PdpSharedDeposit_item
            skus @include(if: $isMultiSku) {
                serviceId
                displayInfo(page: pdp, buyerId: $userId) {
                    ctas {
                        showPurchase
                        showMakeOffer
                        showRequestPrice
                        showFollowCategory
                        showAcceptOffer
                    }
                }
            }
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarButtons_displayInfo on ItemDisplayInfo {
            ctas {
                showPurchase
                showMakeOffer
                showRequestPrice
                showFollowCategory
                showAcceptOffer
            }
        }
    `,
});
